/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from "react";
import { isSplit } from "shared/utils/validation";
import styles from "./cardByLaneV2.module.scss";

const RenderThrow = ({ value, color, isFrameTen, strikeBgColor }) => {
  const strikeStyle = isFrameTen
    ? {
        position: "relative",
        width: "auto",
        backgroundColor: "transparent",
      }
    : {
        backgroundColor: strikeBgColor,
      };

  if (value === "X") {
    return (
      <span className={styles.strike} style={strikeStyle}>
        <svg
          width="21"
          height="17"
          viewBox="0 0 21 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="0.5"
            y="0.5"
            width="20"
            height="16"
            rx="3"
            fill={color || "#000"}
          />
          <path
            d="M13.5 5.5L7.5 11.5M7.5 5.5L13.5 11.5"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </span>
    );
  }
  if (value === "/") {
    return (
      <svg width="11" height="14" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M8.785.333h.77a1 1 0 0 1 1 1v11.334a1 1 0 0 1-1 1H.978a1 1 0 0 1-.823-1.568L7.96.766a1 1 0 0 1 .824-.433Z"
          fill={color || "#000"}
        />
      </svg>
    );
  }
  return value || "";
};

const CardLaneV2 = ({ player, theme }) => {
  const { throws, scores, playerName, game, scoreCompletedGames } = player;

  // Theme Style Declarations
  const containerTheme = useMemo(
    () => ({
      fontFamily: `${theme.font_family}, sans-serif`,
      backgroundColor: theme.section_container_background,
    }),
    [theme.section_container_background, theme.font_family],
  );

  const playerNameTheme = useMemo(
    () => ({
      color: theme.player_name.font_color,
      fontFamily: `${theme.player_name.font_family}, sans-serif`,
      fontSize: theme.player_name.font_size,
      fontWeight: theme.player_name.font_bold ? "bold" : "normal",
      fontStyle: theme.player_name.font_style,
    }),
    [theme.player_name],
  );

  const gameNumberTheme = useMemo(
    () => ({
      fontSize: theme.game_label.font_size || "18px",
      fontFamily: `${
        theme.game_label.font_family || "Futura PT Cond"
      }, sans-serif`,
      color: theme.game_label.font_color || "#FFF",
      fontWeight: theme.game_label.font_bold ? "bold" : "normal",
    }),
    [theme.game_label],
  );

  const frameScoreTheme = useMemo(
    () => ({
      color: theme.frame_score.font_color,
      fontSize: theme.frame_score.font_size,
      fontWeight: theme.frame_score.font_bold ? "bold" : "normal",
      backgroundColor: theme.scores_container_background,
    }),
    [theme.frame_score],
  );

  const throwsTheme = useMemo(
    () => ({
      color: theme.throws_style.font_color,
      fontSize: theme.throws_style.font_size,
      fontWeight: theme.throws_style.font_bold ? "bold" : "normal",
      "--background-odd": theme.scores_container_background,
      "--background-even": theme.top_frame_background,
    }),
    [
      theme.throws_style,
      theme.scores_container_background,
      theme.top_frame_background,
    ],
  );

  const totalScoreTheme = useMemo(
    () => ({
      color: theme.total_score.color || theme.total_score.font_color,
      fontFamily: `${theme.total_score.font_family}, sans-serif`,
      fontSize: theme.total_score.font_size,
      fontWeight:
        theme.total_score.font_weight || theme.total_score.font_bold
          ? "bold"
          : "normal",
      letterSpacing: "1px",
    }),
    [theme.total_score],
  );

  const totalLabelTheme = useMemo(
    () => ({
      color: theme.total_label.color || theme.total_label.font_color,
      fontFamily: `${theme.total_label.font_family}, sans-serif`,
      fontSize: theme.total_label.font_size,
      fontWeight:
        theme.total_label.font_weight || theme.total_label.font_bold
          ? "bold"
          : "normal",
    }),
    [theme.total_label, theme.total_score.font_family],
  );
  // End of Theme Style Declarations

  return (
    <section className={styles.container} style={containerTheme}>
      <div className={styles.player}>
        <p style={playerNameTheme}>{playerName}</p>
        <p style={gameNumberTheme}>Game {game}</p>
      </div>
      <div style={{ display: "flex" }}>
        {/*Frames container*/}
        <ul className={styles.frames}>
          {Array.from({ length: 10 }).map((_, index) => (
            <li key={index} className={styles.frame}>
              {/* render two (three for 10th frame) cells for every index */}
              <div style={throwsTheme}>
                <div
                  className={`${styles.throw} ${
                    isSplit(player.pins[index * 2]) ? styles.split : ""
                  }`}
                >
                  <RenderThrow
                    value={throws[index * 2]}
                    isFrameTen={index >= 9}
                    strikeBgColor={theme.scores_container_background}
                    color={theme.throws_style.font_color}
                  />
                </div>
                <div className={styles.throw}>
                  <RenderThrow
                    value={throws[index * 2 + 1]}
                    isFrameTen={index >= 9}
                    strikeBgColor={theme.scores_container_background}
                    color={theme.throws_style.font_color}
                  />
                </div>

                {/* render a third throw for the 10th frame */}
                {index === 9 && (
                  <div className={styles.throw}>
                    <RenderThrow
                      value={throws[index * 2 + 2]}
                      isFrameTen={index >= 9}
                      strikeBgColor={theme.scores_container_background}
                      color={theme.throws_style.font_color}
                    />
                  </div>
                )}
              </div>

              {/* render the score cell */}
              <div className={styles.frameScore} style={frameScoreTheme}>
                {scores[index]}
              </div>
            </li>
          ))}
        </ul>

        {/*Total block*/}
        <div
          className={styles.total}
          style={{ backgroundColor: theme.total_score.background_color }}
        >
          <h3 style={totalScoreTheme}>
            {scoreCompletedGames +
              (player.scores[player.scores.length - 1] || 0)}
          </h3>
          <p style={totalLabelTheme}>Total</p>
        </div>
      </div>
    </section>
  );
};

export default CardLaneV2;
